"use client"

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import { useNotification } from '../../contexts/NotificationContext';

const statusColors = {
  draft: 'default',
  open: 'primary',
  paid: 'success',
  void: 'error',
  uncollectible: 'warning',
};

const Invoices = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    customerId: '',
  });

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          ...filters,
        },
      });
      
      if (response.data && response.data.data) {
        setInvoices(response.data.data);
        setTotal(response.data.count || response.data.data.length);
      } else {
        setInvoices([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showNotification('Error fetching invoices', 'error');
      setInvoices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [page, rowsPerPage, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    });
    setPage(0);
  };

  return (
    <Box>
      <PageHeader
        title="Invoices"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/billing/invoices/create')}
          >
            Create Invoice
          </Button>
        }
      />

      <Card>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              name="status"
              label="Status"
              value={filters.status}
              onChange={handleFilterChange}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="void">Void</MenuItem>
              <MenuItem value="uncollectible">Uncollectible</MenuItem>
            </TextField>
          </Stack>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice._id}>
                    <TableCell>{invoice.number}</TableCell>
                    <TableCell>
                      {invoice.customerId ? invoice.customerId.name : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: invoice.currency,
                      }).format(invoice.total)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        color={statusColors[invoice.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(invoice.createdAt), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      {invoice.dueDate
                        ? format(new Date(invoice.dueDate), 'MMM dd, yyyy')
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => navigate(`/billing/invoices/${invoice._id}`)}
                        size="small"
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </Box>
  );
};

export default Invoices;

